import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  KeyRound,
  Languages,
  LayoutDashboard,
  LogOut,
  Menu,
  RadioTower,
  Settings2,
  ShieldCheck,
  UserRound,
  UsersRound,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import ThemeSwitcher from '../components/ThemeSwitcher';

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => setSidebarOpen(false), [location.pathname]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const lang = i18n.resolvedLanguage ?? i18n.language;
  const toggleLang = () => i18n.changeLanguage(lang === 'ru' ? 'en' : 'ru');
  const navItems = [
    { to: '/admin', end: true, icon: LayoutDashboard, label: t('adminLayout.navOverview') },
    { to: '/admin/devices', icon: RadioTower, label: t('adminLayout.navDevices') },
    { to: '/admin/tokens', icon: KeyRound, label: t('adminLayout.navTokens') },
    { to: '/admin/users', icon: UsersRound, label: t('adminLayout.navUsers') },
    { to: '/admin/settings', icon: Settings2, label: t('adminLayout.navSettings') },
  ];

  const brand = (
    <button type="button" className="brand" onClick={() => navigate('/admin')} aria-label={t('adminLayout.navOverview')}>
      <img className="brand-logo" src="/logo.png" alt="" />
      <span className="brand-copy">
        <span className="brand-name">Geek Automation System</span>
        <span className="brand-context">{t('adminLayout.adminPanel')}</span>
      </span>
    </button>
  );

  return (
    <div className="admin-shell">
      <header className="mobile-admin-bar">
        {brand}
        <button type="button" className="icon-btn" onClick={() => setSidebarOpen(true)} aria-label={t('common.openNavigation')} aria-expanded={sidebarOpen}>
          <Menu className="icon-lg" aria-hidden="true" />
        </button>
      </header>

      {sidebarOpen && <button type="button" className="sidebar-overlay" onClick={() => setSidebarOpen(false)} aria-label={t('common.closeNavigation')} />}
      <aside className={`sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="sidebar-brand d-flex align-items-center justify-content-between gap-2">
          {brand}
          <button type="button" className="icon-btn sidebar-close" onClick={() => setSidebarOpen(false)} aria-label={t('common.closeNavigation')}>
            <X className="icon" aria-hidden="true" />
          </button>
        </div>

        <nav className="sidebar-nav" aria-label={t('common.mainNavigation')}>
          {navItems.map(({ to, end, icon: Icon, label }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}>
              <Icon className="icon" aria-hidden="true" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <ThemeSwitcher placement="top" className="w-100 mb-2" />
          <button type="button" className="user-chip btn btn-outline-secondary w-100 justify-content-start mb-2" onClick={() => navigate('/admin/profile')} title={t('userLayout.profile')}>
            <span className="avatar"><UserRound className="icon" aria-hidden="true" /></span>
            <span className="user-meta text-start">
              <span className="user-name">{user?.username}</span>
              <span className="user-role"><ShieldCheck className="icon d-inline me-1" aria-hidden="true" />{t('adminLayout.administrator')}</span>
            </span>
          </button>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-outline-secondary flex-fill" onClick={toggleLang} title={t('common.switchLanguage')}>
              <Languages className="icon" aria-hidden="true" /> {lang === 'ru' ? 'EN' : 'RU'}
            </button>
            <button type="button" className="btn btn-outline-secondary flex-fill" onClick={handleLogout}>
              <LogOut className="icon" aria-hidden="true" /> {t('adminLayout.logout')}
            </button>
          </div>
        </div>
      </aside>

      <main className="app-content"><Outlet /></main>
    </div>
  );
}
